/**
 * Iter-334 Phase A — Public Vendor Profile page.
 * URL: /vendor/:slug
 *
 * Guest-viewable — no login required. Displays vendor info, packages,
 * portfolio, reviews. "Book Now" launches Stripe Checkout for instant
 * packages, "Request Quote" for quote-type packages.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, MapPin, Globe, Star, Calendar, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function VendorPublicPage() {
  const { slug } = useParams();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [form, setForm] = useState({ guest_name: '', guest_email: '', guest_phone: '', event_start_at: '', event_end_at: '', event_type: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/marketplace/vendors/${slug}`);
      setVendor(r.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error('Failed to load vendor page');
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Fetch availability for the currently-picked month
  useEffect(() => {
    const d = form.event_start_at ? new Date(form.event_start_at) : new Date();
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    axios.get(`${API}/marketplace/vendors/${slug}/availability?month=${month}`)
      .then(r => setUnavailable(r.data.unavailable_dates || []))
      .catch(() => {});
  }, [slug, form.event_start_at]);

  const submit = async () => {
    if (!selectedPkg) { toast.error('Choose a package'); return; }
    if (!form.event_start_at) { toast.error('Pick an event date'); return; }
    if (!form.guest_email) { toast.error('Enter your email'); return; }
    // Client-side conflict check
    const day = form.event_start_at.slice(0, 10);
    if (unavailable.includes(day)) {
      toast.error('That date is already booked. Please choose another.');
      return;
    }
    setBusy(true);
    try {
      const path = selectedPkg.booking_type === 'instant' ? 'instant-book' : 'quote-request';
      const res = await axios.post(`${API}/marketplace/vendors/${slug}/${path}`, {
        package_id: selectedPkg.id,
        ...form,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      else {
        toast.success('Quote request sent! The vendor will follow up shortly.');
        setSelectedPkg(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-[100dvh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  if (notFound) return <div className="min-h-[100dvh] flex items-center justify-center text-slate-600">Vendor not found.</div>;

  const avgRating = vendor.reviews.length ? (vendor.reviews.reduce((s, r) => s + r.rating, 0) / vendor.reviews.length).toFixed(1) : null;

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      {vendor.cover_url && <div className="h-56 sm:h-72 bg-cover bg-center" style={{ backgroundImage: `url(${vendor.cover_url})` }} />}

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-start gap-4">
          {vendor.logo_url && <img src={vendor.logo_url} alt="" className="w-16 h-16 rounded-lg object-cover border-4 border-white -mt-12 shadow" />}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="vendor-name">{vendor.company_name}</h1>
            {vendor.tagline && <div className="text-slate-600 mt-1">{vendor.tagline}</div>}
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-600">
              {vendor.category && <span className="text-teal-700 font-medium">{vendor.category.name}</span>}
              {(vendor.city || vendor.state) && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[vendor.city, vendor.state].filter(Boolean).join(', ')}</span>}
              {vendor.website_url && <a href={vendor.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-teal-700"><Globe className="w-3.5 h-3.5" />Website</a>}
              {avgRating && <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />{avgRating} ({vendor.reviews.length})</span>}
            </div>
          </div>
        </div>

        {vendor.bio_html && (
          <Card className="p-4">
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: vendor.bio_html }} />
          </Card>
        )}

        <section>
          <h2 className="text-lg font-semibold mb-3">Packages</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {vendor.packages.map((pkg) => (
              <Card key={pkg.id} className="p-4" data-testid={`pkg-${pkg.id}`}>
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{pkg.title}</div>
                    {pkg.description && <div className="text-sm text-slate-600 mt-1">{pkg.description}</div>}
                    {pkg.inclusions?.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs text-slate-600">
                        {pkg.inclusions.map((i, idx) => (<li key={idx} className="flex gap-1"><CheckCircle2 className="w-3 h-3 text-teal-600 mt-0.5" />{i}</li>))}
                      </ul>
                    )}
                  </div>
                  <div className="text-right ml-3">
                    <div className="text-xl font-bold text-teal-700">{fmt(pkg.base_price)}</div>
                    {pkg.duration_minutes && <div className="text-xs text-slate-500">{pkg.duration_minutes} min</div>}
                  </div>
                </div>
                <Button size="sm" className="mt-3 w-full bg-teal-600 hover:bg-teal-700" onClick={() => setSelectedPkg(pkg)} data-testid={`select-pkg-${pkg.id}`}>
                  {pkg.booking_type === 'instant' ? 'Book Now' : 'Request Quote'}
                </Button>
              </Card>
            ))}
          </div>
        </section>

        {vendor.media.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Portfolio</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {vendor.media.map((m) => (
                <div key={m.id} className="aspect-square bg-slate-200 bg-cover bg-center rounded" style={{ backgroundImage: `url(${m.thumbnail_url || m.url})` }} />
              ))}
            </div>
          </section>
        )}

        {vendor.reviews.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Reviews</h2>
            <div className="space-y-3">
              {vendor.reviews.map((r) => (
                <Card key={r.id} className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    {[...Array(5)].map((_, i) => (<Star key={i} className={`w-4 h-4 ${i < r.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />))}
                    <span className="font-medium text-sm ml-2">{r.reviewer_name || 'Anonymous'}</span>
                  </div>
                  {r.title && <div className="font-semibold text-sm">{r.title}</div>}
                  {r.body && <div className="text-sm text-slate-600 mt-1">{r.body}</div>}
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>

      {selectedPkg && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" onClick={() => setSelectedPkg(null)}>
          <Card className="w-full sm:max-w-md bg-white p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
            <div className="font-semibold">{selectedPkg.booking_type === 'instant' ? 'Book' : 'Request quote for'}: {selectedPkg.title}</div>
            <div className="text-xl font-bold text-teal-700">{fmt(selectedPkg.base_price)}</div>
            <div><Label className="text-xs">Your name</Label><Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="book-name" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid="book-email" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} /></div>
            <div><Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> Event date + time</Label><Input type="datetime-local" value={form.event_start_at} onChange={(e) => setForm({ ...form, event_start_at: e.target.value })} data-testid="book-date" /></div>
            {form.event_start_at && unavailable.includes(form.event_start_at.slice(0, 10)) && (
              <div className="text-xs text-red-600" data-testid="date-unavailable-warning">
                This date is already booked. Please choose another date.
              </div>
            )}
            <div><Label className="text-xs">Event type (optional)</Label><Input value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} placeholder="Wedding, corporate, birthday…" /></div>
            <div><Label className="text-xs">Message (optional)</Label><Textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything the vendor should know?" /></div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setSelectedPkg(null)} className="flex-1">Cancel</Button>
              <Button onClick={submit} disabled={busy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-testid="book-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (selectedPkg.booking_type === 'instant' ? 'Continue to payment' : 'Send request')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
